import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { TopicSitemapPageCombinedState, TopicSitemapPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { TopicSitemapPageDataContextProvider } from './pageData';

export type ErrorHandlerProps = { context: ErrorStateDetails<TopicSitemapPageCombinedState> };

type Props = {
	prefetchedData: TopicSitemapPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function TopicSitemapPageContextProvider({
	children,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;

	return (
		<TopicSitemapPageDataContextProvider pageData={pageData}>
			<TrackingContext properties={{}}>{children}</TrackingContext>
		</TopicSitemapPageDataContextProvider>
	);
}
